import "@/App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import { DashboardLayout, ALLOWED_ROUTES, FREE_PLAN_LOCKED_PATHS } from "./components/DashboardLayout";
import { isFreePlanPathLocked } from "./founder/intelligenceNav";
import { workspaceContextAPI } from "./lib/api";
import { toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import { GLIMState } from "./brand";

// DEV/QA only: visual-system review page, compiled out of normal builds.
// Enable with REACT_APP_BRAND_QA=true (see pages/dev/BrandQAPage.js).
const BrandQAPage = process.env.REACT_APP_BRAND_QA === 'true'
  ? require("./pages/dev/BrandQAPage").default // eslint-disable-line global-require
  : null;
import ArchitectureExplorerPage from "./pages/ArchitectureExplorerPage";
import SnapshotPage from "./pages/public/SnapshotPage";
import EarlyAccessPage from "./pages/public/EarlyAccessPage";
import BrandDiscoveryPage from "./pages/public/BrandDiscoveryPage";
import DashboardPage from "./pages/DashboardPage";
import DiscoveryRadarPage from "./pages/DiscoveryRadarPage";
import ClientsPage from "./pages/ClientsPage";
import SEOPage from "./pages/SEOPage";
import AdsPage from "./pages/AdsPage";
import WebsitePage from "./pages/WebsitePage";
import AgentsPage from "./pages/AgentsPage";
import SearchPage from "./pages/SearchPage";
import GEOReportPage from "./pages/GEOReportPage";
import TeamPage from "./pages/TeamPage";
import CreativeLabPage from "./pages/CreativeLabPage";
import CreativeProPage from "./pages/CreativeProPage";
import CreativeIntelligencePage from "./pages/CreativeIntelligencePage";
import SearchEverywhereIntelPage from "./pages/SearchEverywhereIntelPage";
import BenchmarkPage from "./pages/BenchmarkPage";
import SoMPage from "./pages/SoMPage";
import SoMPromptsPage from "./pages/SoMPromptsPage";
import SoMHistoryPage from "./pages/SoMHistoryPage";
import SoMUsagePage from "./pages/SoMUsagePage";
import VoicePage from "./pages/VoicePage";
import EntityPage from "./pages/EntityPage";
import AITrafficPage from "./pages/AITrafficPage";
import SearchScorePage from "./pages/SearchScorePage";
import CreatorsPage from "./pages/CreatorsPage";
import VisualSearchPage from "./pages/VisualSearchPage";
import VaultPage from "./pages/VaultPage";
import MarketplacePage from "./pages/MarketplacePage";
import DTRIPage from "./pages/DTRIPage";
import CreatorWorkspacePage from "./pages/CreatorWorkspacePage";
import CreatorContentIntelPage from "./pages/creator/ContentIntelPage";
import CreatorAffiliateRevenuePage from "./pages/creator/AffiliateRevenuePage";
import CreatorLinkedInRevenuePage from "./pages/creator/LinkedInRevenuePage";
import CreatorMarketplaceCardPage from "./pages/creator/MarketplaceCardPage";
import CreatorPitchDeckPage from "./pages/creator/PitchDeckPage";
import CreatorBrandEnquiriesPage from "./pages/creator/BrandEnquiriesPage";

// AF-3.16 — Brand Growth OS pages
import BrandCommandCenterPage from "./pages/brand/BrandCommandCenterPage";
import OpportunityPortfolioPage from "./pages/brand/OpportunityPortfolioPage";
import SignalsPage from "./pages/brand/SignalsPage";
import RecommendationsPage from "./pages/brand/RecommendationsPage";
import PaidMediaPage from "./pages/brand/PaidMediaPage";
import EcommercePage from "./pages/brand/EcommercePage";
import SearchDataPage from "./pages/brand/SearchDataPage";
import CompetitorsPage from "./pages/brand/CompetitorsPage";
import OutcomesPage from "./pages/brand/OutcomesPage";
import EntitiesPage from "./pages/brand/EntitiesPage";
import DiagnosesPage from "./pages/brand/DiagnosesPage";
import ActionPlansPage from "./pages/brand/ActionPlansPage";
import ExperimentCommandCenterPage from "./pages/brand/ExperimentCommandCenterPage";
import LearningCenterPage from "./pages/brand/LearningCenterPage";
import EnvironmentIntelligencePage from "./pages/brand/EnvironmentIntelligencePage";
import CreatorCommandCenterPage from "./pages/brand/CreatorCommandCenterPage";
import DataConnectionsPage from "./pages/brand/DataConnectionsPage";

// AF-3.20 — onboarding + real-brand readiness gate
import BrandOnboardingPage from "./pages/brand/BrandOnboardingPage";
import BrandOverviewPage from "./pages/brand/BrandOverviewPage";
import RealBrandReadinessPage from "./pages/brand/RealBrandReadinessPage";
import SahibBriefingPage from "./pages/brand/SahibBriefingPage";
// Milestone 7A (Unified Intelligence Workspace)
import CompanyContextPage from "./pages/brand/CompanyContextPage";

// Phase 1 (Founder-First UX) — the founder shell and its six surfaces
import { FounderShell } from "./founder/FounderShell";
import FounderBriefingPage from "./founder/pages/BriefingPage";
import FounderDecisionsPage from "./founder/pages/DecisionsPage";
import FounderDecisionPage from "./founder/pages/DecisionPage";
import FounderWorkPage from "./founder/pages/WorkPage";
import FounderResultsPage from "./founder/pages/ResultsPage";
import FounderBusinessPage from "./founder/pages/BusinessPage";
import FounderWatchlistPage from "./founder/pages/WatchlistPage";
import FounderEvidencePage from "./founder/pages/EvidencePage";
import FounderConnectionsPage from "./founder/pages/ConnectionsPage";
import FounderSearchDemandPage from "./founder/pages/SearchDemandPage";
import FounderAnalyticsPage from "./founder/pages/AnalyticsPage";

// AF-3.19 — AI Discovery pillar + Creator chain pages
import AIDiscoveryCommandCenterPage from "./pages/brand/AIDiscoveryCommandCenterPage";
import AIRawDataPage from "./pages/brand/AIRawDataPage";
import CreatorStrategyPage from "./pages/brand/CreatorStrategyPage";
import CreatorIdeasPage from "./pages/brand/CreatorIdeasPage";
import CreatorReviewPage from "./pages/brand/CreatorReviewPage";
import CreatorDecisionsPage from "./pages/brand/CreatorDecisionsPage";
import CreatorProductionPage from "./pages/brand/CreatorProductionPage";
import CreatorDistributionPage from "./pages/brand/CreatorDistributionPage";

// cinematic story prototype: lazy, so three.js stays out of the main bundle
const StoryPreviewPage = lazy(() => import("./pages/public/StoryPreviewPage"));

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[var(--warm-canvas)] flex items-center justify-center" data-testid="app-boot-loading"><GLIMState state="thinking" size={88} label="Loading" /></div>;
  return user ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  // Final landing-page reset -- lets LoginPage hand off a one-time
  // post-auth destination (e.g. /onboarding?domain=... after a fresh
  // registration, so the domain captured on the homepage survives into
  // the real onboarding screen) via sessionStorage rather than a direct
  // navigate() call, which loses a real render-order race against this
  // very redirect (PublicRoute is LoginPage's parent, so it always
  // re-renders and swaps LoginPage out in the same commit where `user`
  // becomes truthy, before LoginPage's own code can act on that
  // change). The read is a PURE render-time read (no mutation) --
  // clearing it is a separate effect below. Found by testing, not
  // assumed: doing the read-then-removeItem inline in the render body
  // broke under React.StrictMode's double-invocation (index.js) --  the
  // first pass consumed it correctly, the second throwaway pass saw it
  // already gone and fell through to "/" instead.
  const redirect = user ? sessionStorage.getItem('martian_post_auth_redirect') : null;
  useEffect(() => {
    if (redirect) sessionStorage.removeItem('martian_post_auth_redirect');
  }, [redirect]);
  if (loading) return <div className="min-h-screen bg-[var(--warm-canvas)] flex items-center justify-center" data-testid="app-boot-loading"><GLIMState state="thinking" size={88} label="Loading" /></div>;
  if (!user) return children;
  return <Navigate to={redirect || '/'} replace />;
};

// AF-3.20.6 — the root path "/" now serves two audiences: a logged-out
// visitor gets the public marketing LandingPage (genuinely accessible
// without authentication, unlike ProtectedRoute's forced /login
// redirect), while an authenticated user gets exactly the same
// HomeRouter persona-branching behavior as before -- unchanged, untouched.
// This is the only routing change this stage makes.
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[var(--canvas-secondary)] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" /></div>;
  // Pre-launch: logged-out visitors get the cinematic story in coming-soon mode
  // (waitlist CTA only, no console entry points). /login stays reachable by URL.
  if (user) return <HomeRouter />;
  return <Suspense fallback={<div className="min-h-screen bg-[#0a0a0a]" />}><StoryPreviewPage comingSoon /></Suspense>;
};

// Route guard: redirect cross-persona access
// AF-3.20.5.1 -- a missing/malformed tenant_type must fail toward the
// restricted, external-facing "brand" surface, never toward "admin"
// (which sees every route and every legacy/persona-portal nav item
// unfiltered). Defaulting to 'admin' here silently promoted any account
// with an unset tenant_type to maximal exposure -- the exact failure
// mode reported in AF-3.20.5.1 (only NAV_CONFIGS.admin contains both
// "MORE (LEGACY)" and "CREATOR PERSONA PORTAL" together).
const PersonaGuard = ({ children, allowed }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantType = user?.tenant_type || 'brand';
  // Free Evaluation Workspace -- the sidebar's lock icon only stops a
  // click; without this, direct navigation (typing the URL, a stale
  // bookmark) still reached the raw, empty AI Visibility page for a
  // brand-new free-plan tenant. Mirrors the exact same redirect+toast
  // pattern as the cross-persona check below, just keyed on plan
  // instead of persona.
  const isFreePlanLocked = isFreePlanPathLocked(location.pathname, user, FREE_PLAN_LOCKED_PATHS);

  useEffect(() => {
    if (tenantType === 'admin' || tenantType === 'master') return; // Admin sees everything
    if (isFreePlanLocked) {
      toast('This unlocks in your full Martian workspace', { description: 'Activate your workspace from Your Martian Briefing to see it.' });
      navigate('/', { replace: true });
      return;
    }
    if (allowed && !allowed.includes(tenantType)) {
      // Not keyed by the viewer's own persona: that told a blocked brand user
      // the section was for brand accounts.
      toast.error("That section isn't available for your account.");
      navigate('/', { replace: true });
    }
  }, [tenantType, allowed, navigate, location.pathname, isFreePlanLocked]);

  if (tenantType === 'admin' || tenantType === 'master') return children;
  if (isFreePlanLocked) return null;
  if (allowed && !allowed.includes(tenantType)) return null;
  return children;
};

// Home screen router by persona
// Milestone 6 (AI CGO Experience Integration) — supersedes AF-3.20.5.2.
// That stage's own audit (this same milestone) found the AI CGO loop
// (Milestones 1-5: real evidence -> finding -> prepared work -> HITL
// review, entirely built into SahibBriefingPage) was reachable ONLY for
// a still-on-`plan:'free'` tenant -- the instant a brand activated
// (POST /auth/activate-full-workspace), HomeRouter silently swapped
// them into BrandOverviewPage, a separate, older pipeline
// (controlPlaneAPI/entity-graph control plane) with zero knowledge that
// prepared_work exists, and nothing anywhere in the nav linked back.
// The brand branch below now always renders SahibBriefingPage,
// regardless of plan -- "/" is Martian Home for every brand tenant, full
// stop. BrandOverviewPage is not deleted or rewritten (explicitly out
// of scope this milestone): it now has its own permanent route,
// /overview, and its own primary-nav entry ("Executive Overview",
// DashboardLayout.js), so it remains fully available as a deeper
// workspace surface -- just no longer the thing "/" silently becomes
// once a brand pays.
const HomeRouter = () => {
  const { user } = useAuth();
  // AF-3.20.5.1 -- same fail-safe-to-brand rationale as PersonaGuard above.
  const tenantType = user?.tenant_type || 'brand';
  if (tenantType === 'creator') return <CreatorWorkspacePage />;
  if (tenantType === 'agency' || tenantType === 'admin' || tenantType === 'master') return <ClientsPage />;
  // Phase 1 -- the founder's home is the new Briefing inside the founder
  // shell. SahibBriefingPage is no longer a brand home (file kept; other
  // tests and the admin history still reference it).
  return <FounderShell><FounderBriefingPage /></FounderShell>;
};

// Phase 1 -- the six founder surfaces for an admin inside a verified
// workspace (/workspace/:clientId/<segment>). The brand persona's own
// top-level routes are declared literally in <Routes> below.
const WORKSPACE_FOUNDER_SEGMENTS = [
  ['decisions', <FounderDecisionsPage />],
  ['decisions/:decisionId', <FounderDecisionPage />],
  ['work', <FounderWorkPage />],
  ['work/create', <FounderWorkPage view="create" />],
  ['results', <FounderResultsPage />],
  ['business', <FounderBusinessPage />],
  ['business/watchlist', <FounderWatchlistPage />],
  ['evidence', <FounderEvidencePage />],
  ['evidence/connections', <FounderConnectionsPage />],
  ['search-demand', <FounderSearchDemandPage />],
  ['analytics', <FounderAnalyticsPage />],
];

const founderRoute = (element) => (
  <ProtectedRoute><PersonaGuard allowed={['brand']}><FounderShell>{element}</FounderShell></PersonaGuard></ProtectedRoute>
);

// Phase 1 -- the founder no longer navigates the backend pipeline
// (signals -> diagnoses -> opportunities -> recommendations -> action
// plans). For the brand persona those addresses redirect to the surface
// that now answers the same question; every other persona still gets the
// original page, unchanged.
const BrandRedirect = ({ to, children }) => {
  const { user } = useAuth();
  if ((user?.tenant_type || 'brand') === 'brand') return <Navigate to={to} replace />;
  return children;
};

const WorkspaceContextDenied = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto text-center py-16" data-testid="workspace-context-denied">
      <p className="text-[13.5px] text-[var(--text-secondary)] mb-4">
        This account doesn't have access to that workspace.
      </p>
      <button
        onClick={() => navigate('/')}
        className="text-[12.5px] font-semibold text-[var(--accent)]"
        data-testid="workspace-context-denied-back"
      >
        Back to Admin Workspace
      </button>
    </div>
  );
};

// Milestone 6.1 (Explicit Workspace Context) -- identity != active
// workspace context. This route never swaps who the caller is
// authenticated as (no token change, before or after); it marks the
// deliberate moment an admin account enters a specific, already-owned
// workspace. On mount, calls POST /workspace-context/enter, which
// re-verifies real ownership via the exact same get_verified_client
// every other client-scoped route uses (server-side, not trusted from
// the URL alone) and records a real audit event. Only on that success
// does SahibBriefingPage render, with workspaceClientId set so it shows
// this specific workspace's real data and the DashboardLayout context
// banner. A client_id this account doesn't own fails exactly like any
// other 404 -- an honest state here, never a silent redirect.
const WorkspaceContextEntry = () => {
  const { clientId } = useParams();
  const [status, setStatus] = useState('entering'); // entering | ready | denied

  useEffect(() => {
    let cancelled = false;
    setStatus('entering');
    // The response's own client name isn't used here -- SahibBriefingPage
    // fetches its own real data (including the name) once workspaceClientId
    // is set below; this call exists purely to verify ownership and
    // record the audit event before anything renders.
    workspaceContextAPI.enter(clientId)
      .then(() => { if (!cancelled) setStatus('ready'); })
      .catch(() => { if (!cancelled) setStatus('denied'); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (status === 'entering') {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto text-center py-16 text-[13px] text-[var(--text-tertiary)]" data-testid="workspace-context-entering">
          Opening workspace…
        </div>
      </DashboardLayout>
    );
  }

  if (status === 'denied') {
    // Deliberately not an auto-redirect -- an honest state the user
    // sees and dismisses themselves, never a silent bounce that could
    // look like nothing happened.
    return (
      <DashboardLayout>
        <WorkspaceContextDenied />
      </DashboardLayout>
    );
  }

  // Phase 1 -- the admin sees the same founder surfaces the brand sees,
  // for this verified workspace only (fixedClientId), with every link kept
  // inside /workspace/:clientId.
  const base = `/workspace/${clientId}`;
  return (
    <FounderShell fixedClientId={clientId} basePath={base} key={clientId}>
      <Routes>
        <Route index element={<FounderBriefingPage />} />
        {WORKSPACE_FOUNDER_SEGMENTS.map(([segment, element]) => <Route key={segment} path={segment} element={element} />)}
        <Route path=":unknown/*" element={<Navigate to={base} replace />} />
      </Routes>
    </FounderShell>
  );
};

const RTLWrapper = ({ children }) => {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.setAttribute('dir', i18n.language === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);
  return children;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <RTLWrapper>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            {BrandQAPage && <Route path="/__brand-qa" element={<BrandQAPage />} />}
            {/* Internal engineering tool (source-generated architecture graph,
                see scripts/generate_architecture.py). Renders no tenant data
                of its own, only a static graph of the codebase's own
                structure -- but the graph itself (route names, service
                names, data-layer shape) is internal information an external
                design partner has no reason to see. Final Design Partner
                Release Gate: gated behind the same ProtectedRoute +
                PersonaGuard(admin/master) pattern /workspace/:clientId
                already uses, rather than left unguarded -- an unauthenticated
                or brand/agency/creator visitor is redirected (to /login, or
                to / with a toast) exactly like any other admin-only route;
                a real admin/master login still reaches it unchanged. */}
            <Route path="/architecture" element={<ProtectedRoute><PersonaGuard allowed={['admin', 'master']}><ArchitectureExplorerPage /></PersonaGuard></ProtectedRoute>} />
            {/* Final landing-page reset -- step 2 of Homepage -> Snapshot ->
                Signup. Unguarded like LandingPage itself: viewable whether
                logged in or not, no redirect logic needed. */}
            <Route path="/snapshot" element={<SnapshotPage />} />
            <Route path="/early-access" element={<EarlyAccessPage />} />
            <Route path="/story-preview" element={<Suspense fallback={null}><StoryPreviewPage /></Suspense>} />

            {/* Step 3 of the brand journey: website-only discovery ->
                Company Context review. Unguarded like Snapshot/Early
                Access above -- a logged-out visitor sees only the INPUT
                state (submitting hands off to /login, same pattern
                Snapshot already uses); an authenticated user sees the
                full research/review flow. See BrandDiscoveryPage.js. */}
            <Route path="/discovery" element={<BrandDiscoveryPage />} />

            {/* Home — coming-soon story when logged out, persona-branched HomeRouter when logged in */}
            <Route path="/" element={<RootRoute />} />

            {/* Phase 1 — founder surfaces */}
            <Route path="/decisions" element={founderRoute(<FounderDecisionsPage />)} />
            <Route path="/decisions/:decisionId" element={founderRoute(<FounderDecisionPage />)} />
            <Route path="/work" element={founderRoute(<FounderWorkPage />)} />
            <Route path="/work/create" element={founderRoute(<FounderWorkPage view="create" />)} />
            <Route path="/results" element={founderRoute(<FounderResultsPage />)} />
            <Route path="/business" element={founderRoute(<FounderBusinessPage />)} />
            <Route path="/business/watchlist" element={founderRoute(<FounderWatchlistPage />)} />
            <Route path="/evidence" element={founderRoute(<FounderEvidencePage />)} />
            <Route path="/evidence/connections" element={founderRoute(<FounderConnectionsPage />)} />
            <Route path="/search-demand" element={founderRoute(<FounderSearchDemandPage />)} />
            <Route path="/analytics" element={founderRoute(<FounderAnalyticsPage />)} />

            {/* Shared routes (all personas) */}
            <Route path="/som" element={<ProtectedRoute><SoMPage /></ProtectedRoute>} />
            <Route path="/som/prompts" element={<ProtectedRoute><BrandRedirect to="/business/watchlist"><SoMPromptsPage /></BrandRedirect></ProtectedRoute>} />
            <Route path="/som/history" element={<ProtectedRoute><SoMHistoryPage /></ProtectedRoute>} />
            <Route path="/som/usage" element={<ProtectedRoute><SoMUsagePage /></ProtectedRoute>} />
            <Route path="/discovery-radar" element={<ProtectedRoute><DiscoveryRadarPage /></ProtectedRoute>} />
            <Route path="/vault" element={<ProtectedRoute><VaultPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
            <Route path="/dtri" element={<ProtectedRoute><DTRIPage /></ProtectedRoute>} />

            {/* Brand + Agency routes */}
            <Route path="/seo" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><SEOPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/ads" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><AdsPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/ai-traffic" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><AITrafficPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creators" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><CreatorsPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/clients" element={<ProtectedRoute><PersonaGuard allowed={['agency','master']}><ClientsPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/geo-report" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><GEOReportPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/benchmark" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><BenchmarkPage /></PersonaGuard></ProtectedRoute>} />
            {/* Legacy Tavily tools: agency/admin only. Brand founders use Web Search Visibility (/geo-report); the backend enforces the same persona rule. */}
            <Route path="/search" element={<ProtectedRoute><PersonaGuard allowed={['agency','master']}><SearchPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/search-score" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><SearchScorePage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/voice" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><VoicePage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/entity" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><EntityPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/visual-search" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><VisualSearchPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creative-intelligence" element={<ProtectedRoute><BrandRedirect to="/work/create"><PersonaGuard allowed={['brand','agency','master','admin']}><CreativeIntelligencePage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            <Route path="/search-everywhere" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master','admin']}><SearchEverywhereIntelPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creative-lab" element={<Navigate to="/creative-intelligence" replace />} />
            <Route path="/creative-pro" element={<Navigate to="/creative-intelligence" replace />} />
            <Route path="/marketplace" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><MarketplacePage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/website" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><WebsitePage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/agents" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><AgentsPage /></PersonaGuard></ProtectedRoute>} />

            {/* AF-3.16 — Brand Growth OS routes (brand + agency + admin) */}
            <Route path="/growth/overview" element={<ProtectedRoute><BrandRedirect to="/"><PersonaGuard allowed={['brand','agency','master']}><BrandCommandCenterPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            <Route path="/growth/opportunities" element={<ProtectedRoute><BrandRedirect to="/decisions"><PersonaGuard allowed={['brand','agency','master']}><OpportunityPortfolioPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            {/* Brand Console -- Signals/Recommendations reuse the existing
                 AF-2.2/AF-2.5 backends entirely (no new endpoint); only
                 these two pages are new. */}
            <Route path="/growth/signals" element={<ProtectedRoute><BrandRedirect to="/decisions"><PersonaGuard allowed={['brand','agency','master']}><SignalsPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            {/* Final completeness audit -- Diagnoses/Action Plans reuse the existing AF-2.3/AF-2.6 backends entirely (no new endpoint); only these two pages are new. */}
            <Route path="/growth/diagnoses" element={<ProtectedRoute><BrandRedirect to="/decisions"><PersonaGuard allowed={['brand','agency','master']}><DiagnosesPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            <Route path="/growth/recommendations" element={<ProtectedRoute><BrandRedirect to="/decisions"><PersonaGuard allowed={['brand','agency','master']}><RecommendationsPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            <Route path="/growth/action-plans" element={<ProtectedRoute><BrandRedirect to="/decisions"><PersonaGuard allowed={['brand','agency','master']}><ActionPlansPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            {/* Brand Console DATA -- first real connector-backed surfaces,
                 consuming ONLY the existing intelligence_measurements
                 pipeline (never the legacy fabricated /ads or /marketplace
                 routes). */}
            <Route path="/growth/paid-media" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><PaidMediaPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/growth/ecommerce" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><EcommercePage /></PersonaGuard></ProtectedRoute>} />
            {/* Next real DATA surfaces (not under /growth -- these are DATA_NAV_ITEMS' own /data/* paths): Search (DataForSEO -> intelligence_measurements) and Competitors (existing GET /v2/competitors/{id} viewer). */}
            <Route path="/data/search" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><SearchDataPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/data/competitors" element={<ProtectedRoute><BrandRedirect to="/business"><PersonaGuard allowed={['brand','agency','master']}><CompetitorsPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            {/* Raw Data Console phase 4 -- Entities reuses the existing GET /v3/graph/entities/{id} route as its first standalone viewer. */}
            <Route path="/data/entities" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><EntitiesPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/growth/experiments" element={<ProtectedRoute><BrandRedirect to="/results"><PersonaGuard allowed={['brand','agency','master']}><ExperimentCommandCenterPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            <Route path="/growth/learnings" element={<ProtectedRoute><BrandRedirect to="/results"><PersonaGuard allowed={['brand','agency','master']}><LearningCenterPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            <Route path="/growth/environment" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><EnvironmentIntelligencePage /></PersonaGuard></ProtectedRoute>} />
            {/* Raw Data Console phase 2 -- Outcomes reuses the existing AF-2.8 OutcomeService entirely (GET /intelligence/{workspace_id}/outcomes), zero frontend consumer until now. */}
            <Route path="/growth/outcomes" element={<ProtectedRoute><BrandRedirect to="/results"><PersonaGuard allowed={['brand','agency','master']}><OutcomesPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            <Route path="/growth/creator-command-center" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><CreatorCommandCenterPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/growth/data-connections" element={<ProtectedRoute><BrandRedirect to="/evidence/connections"><PersonaGuard allowed={['brand','agency','master']}><DataConnectionsPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />

            {/* AF-3.20 — onboarding (brand + agency + admin) + readiness gate (admin only) */}
            <Route path="/onboarding" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><BrandOnboardingPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/admin/real-brand-readiness" element={<ProtectedRoute><PersonaGuard allowed={['master']}><RealBrandReadinessPage /></PersonaGuard></ProtectedRoute>} />

            {/* Milestone 6 (AI CGO Experience Integration) — BrandOverviewPage
                 previously had no route of its own at all, only a HomeRouter
                 branch at "/" for non-free brand tenants. That branch is
                 removed below (HomeRouter now always renders
                 SahibBriefingPage for the brand persona, at every plan) --
                 this route gives BrandOverviewPage a real, permanent address
                 so it stays fully reachable as a deeper workspace surface,
                 exactly as approved ("do not delete, merge, or substantially
                 rewrite... existing pages can remain available"). Same
                 reachability as before: brand persona only (admin/master
                 bypass PersonaGuard entirely, unchanged); locked for a
                 free-plan tenant via DashboardLayout.js's brandFree nav
                 entry (new, not in unlockedKeys) + FREE_PLAN_LOCKED_PATHS,
                 which is derived from that config -- no new capability is
                 unlocked for free plan. */}
            <Route path="/overview" element={<ProtectedRoute><BrandRedirect to="/"><PersonaGuard allowed={['brand']}><BrandOverviewPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            {/* Milestone 7A -- brand-persona only for now, same as
                 /overview above; not yet extended into the admin
                 workspace-context journey (explicitly out of this
                 milestone's scope, not an oversight). */}
            <Route path="/company-context" element={<ProtectedRoute><BrandRedirect to="/business"><PersonaGuard allowed={['brand']}><CompanyContextPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />

            {/* Milestone 6.1 (Explicit Workspace Context) -- the admin
                 entry surface into a specific, already-owned workspace's
                 real Martian Home. Deliberately admin/master only: a brand
                 persona already has its own natural Martian Home at "/"
                 (HomeRouter above) and never needs this route. */}
            <Route path="/workspace/:clientId/*" element={<ProtectedRoute><PersonaGuard allowed={['admin','master']}><WorkspaceContextEntry /></PersonaGuard></ProtectedRoute>} />

            {/* AF-3.19 — AI Discovery pillar (brand + agency + admin) */}
            <Route path="/ai-discovery" element={<ProtectedRoute><BrandRedirect to="/"><PersonaGuard allowed={['brand','agency','master']}><AIDiscoveryCommandCenterPage /></PersonaGuard></BrandRedirect></ProtectedRoute>} />
            {/* Raw Data Console -- reuses the existing, real GET
                 /v2/executions/{client_id} and GET
                 /v2/citations/{client_id}/history routes; no new backend
                 endpoint. */}
            <Route path="/ai-discovery/raw-data" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><AIRawDataPage /></PersonaGuard></ProtectedRoute>} />

            {/* AF-3.19 — Creator chain (brand + agency + admin) */}
            <Route path="/creator/strategy" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><CreatorStrategyPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/ideas" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><CreatorIdeasPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/review" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><CreatorReviewPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/decisions" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><CreatorDecisionsPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/production" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><CreatorProductionPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/distribution" element={<ProtectedRoute><PersonaGuard allowed={['brand','agency','master']}><CreatorDistributionPage /></PersonaGuard></ProtectedRoute>} />

            {/* Creator-only routes */}
            <Route path="/creator-workspace" element={<ProtectedRoute><PersonaGuard allowed={['creator','master']}><CreatorWorkspacePage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/content-intelligence" element={<ProtectedRoute><PersonaGuard allowed={['creator','master']}><CreatorContentIntelPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/affiliate-revenue" element={<ProtectedRoute><PersonaGuard allowed={['creator','master']}><CreatorAffiliateRevenuePage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/linkedin-revenue" element={<ProtectedRoute><PersonaGuard allowed={['creator','master']}><CreatorLinkedInRevenuePage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/marketplace-card" element={<ProtectedRoute><PersonaGuard allowed={['creator','master']}><CreatorMarketplaceCardPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/pitch-deck" element={<ProtectedRoute><PersonaGuard allowed={['creator','master']}><CreatorPitchDeckPage /></PersonaGuard></ProtectedRoute>} />
            <Route path="/creator/brand-enquiries" element={<ProtectedRoute><PersonaGuard allowed={['creator','master']}><CreatorBrandEnquiriesPage /></PersonaGuard></ProtectedRoute>} />

            {/* Legacy routes */}
            <Route path="/dashboard-legacy" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" />
      </RTLWrapper>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
